import React, {
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	NormalizedDeviceHardware,
	normalizeDeviceHardware,
	VelavuAPI,
} from "velavu-js-api";
import VelavuButton from "../../../elements/velavu-button";
import VelavuIDInput, {
	VelavuIDInputHandle,
} from "../../../elements/velavu-id-input";
import VelavuNotice from "../../../elements/velavu-notice/velavu-notice";
import * as styles from "./modal-register-tag-id.module.scss";

const tagIDRegex = /^[0-9a-zA-Z]{5}$/;

export interface ModalRegisterTagIDHandle {
	focusInput: () => void;
}

interface ModalRegisterTagIDProps {
	ref?: Ref<ModalRegisterTagIDHandle>;
	id: string;
	setID: React.Dispatch<React.SetStateAction<string>>;
	onSubmit: (id: string) => void;
	loading?: boolean;
	error: string;
}

export default function ModalRegisterTagID(props: ModalRegisterTagIDProps) {
	const { t } = useTranslation();
	const idInputRef = useRef<VelavuIDInputHandle>(null);

	useImperativeHandle(props.ref, () => ({
		focusInput: () => {
			idInputRef.current?.focus();
		},
	}));

	const [isVesta, setIsVesta] = useState<boolean | undefined>(undefined);

	const {
		id: propsID,
		onSubmit: propsOnSubmit,
		loading: propsLoading,
	} = props;

	const inputValid = useMemo(() => {
		return tagIDRegex.test(propsID);
	}, [propsID]);

	const isAvailable = useCallback(async () => {
		try {
			const response = await VelavuAPI.devices.queryDeviceByIDOrSerial({
				id: propsID,
			});

			if (
				normalizeDeviceHardware(response.device.hardware) ===
				NormalizedDeviceHardware.Argo
			) {
				setIsVesta(true);
			} else {
				setIsVesta(false);
			}
		} catch (error) {
			console.warn(error);
		}
	}, [propsID]);

	const handleSubmit = useCallback(
		(event: React.FormEvent) => {
			event.preventDefault();

			//Ignoring if we don't have valid input or we're loading
			if (!inputValid || propsLoading) return;

			if (propsOnSubmit) propsOnSubmit(propsID);
		},
		[inputValid, propsID, propsLoading, propsOnSubmit],
	);

	useEffect(() => {
		if (inputValid) isAvailable();
	}, [inputValid, isAvailable]);

	return (
		<form className={styles.container} onSubmit={handleSubmit}>
			<div className={styles.inputContainer}>
				<VelavuIDInput
					ref={idInputRef}
					value={props.id}
					onChange={props.setID}
					disabled={props.loading}
					error={props.error !== ""}
					autofocus
				/>
			</div>

			{props.error ? (
				<VelavuNotice type="error">{props.error}</VelavuNotice>
			) : (
				<>
					<VelavuNotice type="info">
						{t("devices.register.code_notice")}
					</VelavuNotice>
					<VelavuNotice
						style={{ marginTop: 16 }}
						type={isVesta === false ? "error" : "info"}
					>
						{t("devices.register.vesta_only_notice")}
					</VelavuNotice>
				</>
			)}
			<VelavuButton
				className={styles.button}
				label={t("common.register")}
				disabled={
					(inputValid && !isVesta) ||
					!inputValid ||
					!isVesta ||
					props.loading
				}
				loadingIndicator={props.loading}
				fullWidth
				submit
			/>
		</form>
	);
}
